import React, { useState, useEffect, useRef } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, addDoc, setDoc, doc } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { analyze } from 'web-audio-beat-detector'; // Correct named import
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { app, db, auth, storage } from "../firebase/init";
// use auth from init.js

const SlideUploader = () => {
  const [slideshowTitle, setSlideshowTitle] = useState("");
  const [slides, setSlides] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [bpmValue, setBpmValue] = useState(60); // Default BPM
  const [bpmDetecting, setBpmDetecting] = useState(false); // Add bpmDetecting state
  const [beatsPerSlide, setBeatsPerSlide] = useState(8); // Default beats per slide
  const [aspectRatio, setAspectRatio] = useState("4:3"); // Default aspect ratio set to 4:3
  const [textColor, setTextColor] = useState("#000000"); // Default text color
  const [textFont, setTextFont] = useState("Arial"); // Default font
  const [textSize, setTextSize] = useState(40); // Default text size set to 40px
  const [logoUrl, setLogoUrl] = useState("https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoWhiteTM.png?alt=media&token=6ef84cf6-6fd9-4b56-bf05-aea9a1784c72"); // Default logo set to SBL White Logo
  const [logoSize, setLogoSize] = useState(20); // Default logo size set to 20%
  const [logoPosition, setLogoPosition] = useState("right"); // Default logo position
  const [textAlignment, setTextAlignment] = useState("center"); // Default text alignment set to center
  const [textBackgroundEnabled, setTextBackgroundEnabled] = useState(false); // Text background toggle
  const [textBackgroundColor, setTextBackgroundColor] = useState("#ffffff"); // Default text background color
  const [textBackgroundOpacity, setTextBackgroundOpacity] = useState(1); // Default opacity
  const [audioDuration, setAudioDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [markers, setMarkers] = useState([]); // List of marker timestamps
  const audioRef = useRef(null);

  const navigate = useNavigate(); // Initialize navigate

  const predefinedLogos = [
    { label: "SBL Black Logo", value: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f" },
    { label: "SBL White Logo", value: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoWhiteTM.png?alt=media&token=6ef84cf6-6fd9-4b56-bf05-aea9a1784c72" },
  ];

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const newSlides = files.map((file) => ({
      id: uuidv4(),
      file,
      type: file.type.startsWith("video/") ? "video" : "image",
      previewUrl: URL.createObjectURL(file),
      caption: "",
      order: slides.length + 1,
    }));
    setSlides((prev) => [...prev, ...newSlides]);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    const newSlides = files.map((file) => ({
      id: uuidv4(),
      file,
      type: file.type.startsWith("video/") ? "video" : "image",
      previewUrl: URL.createObjectURL(file),
      caption: "",
      order: slides.length + 1,
    }));
    setSlides((prev) => [...prev, ...newSlides]);
  };

  const handleCaptionChange = (id, value) => {
    setSlides((prev) =>
      prev.map((slide) =>
        slide.id === id ? { ...slide, caption: value } : slide
      )
    );
  };

  const handleOrderChange = (id, value) => {
    setSlides((prev) =>
      prev.map((slide) =>
        slide.id === id ? { ...slide, order: parseInt(value) || 0 } : slide
      )
    );
  };

  const handleDuplicateLocalSlide = (slide) => {
    const duplicate = {
      ...slide,
      id: uuidv4(),
      order: slides.length + 1,
      previewUrl: slide.previewUrl,
    };
    setSlides(prev => [...prev, duplicate]);
  };

  const handleUpload = async () => {
    if (!slideshowTitle.trim()) {
      alert("Please enter a slideshow title.");
      return;
    }

    const user = auth.currentUser;

    if (!user) {
      alert("You must be signed in to upload slides.");
      return;
    }

    setUploading(true);
    try {
      let audioUrl = "";
      let audioStoragePath = "";

      // Upload audio file if provided
      if (audioFile) {
        // DEBUG: log auth + token so we can see it in the Console
  console.log("DEBUG auth.currentUser:", auth.currentUser);
  if (auth.currentUser) {
    const token = await auth.currentUser.getIdToken();
    console.log("DEBUG idToken prefix:", token.slice(0, 20));
  } else {
    console.warn("DEBUG: No auth.currentUser — you are NOT signed in.");
  }
        const audioRef = ref(storage, `slideshows/${slideshowTitle}/music/${audioFile.name}`);
        await uploadBytes(audioRef, audioFile);
        audioUrl = await getDownloadURL(audioRef);
        audioStoragePath = audioRef.fullPath; // Save storage path
      }

      // Save slideshow metadata
      await setDoc(doc(db, `slideshows/${slideshowTitle}`), {
        title: slideshowTitle,
        audioUrl,
        audioStoragePath, // Save audio storage path
        bpm: bpmValue,
        beatsPerSlide, // Save beats per slide
        aspectRatio, // Save aspect ratio
        textColor, // Save text color
        textFont, // Save text font
        textSize, // Save text size
        logoUrl, // Save logo URL
        logoSize, // Save logo size
        logoPosition, // Save logo position
        textAlignment, // Save text alignment
        textBackgroundEnabled, // Save text background toggle
        textBackgroundColor, // Save text background color
        textBackgroundOpacity, // Save text background opacity
      });

      // Upload slides
      const sortedSlides = [...slides].sort((a, b) => a.order - b.order);
      for (const [index, slide] of sortedSlides.entries()) {
        const fileRef = ref(storage, `slideshows/${slideshowTitle}/files/${slide.file.name}`);
        await uploadBytes(fileRef, slide.file);
        const url = await getDownloadURL(fileRef);
        const storagePath = fileRef.fullPath; // Save storage path

        const time = markers[index] || 0; // Use marker timestamp or default to 0
        await addDoc(collection(db, `slideshows/${slideshowTitle}/slides`), {
          imageUrl: url, // ✅ use imageUrl here
          imageStoragePath: storagePath,
          type: slide.type || 'image', // Store type (image or video)
          caption: slide.caption,
          order: slide.order,
          timestamp: time, // Save timestamp
        });
      }
      alert("Slideshow uploaded successfully!");
      setSlides([]);
      setSlideshowTitle("");
      setAudioFile(null);
      setBpmValue(60);
      setBeatsPerSlide(8); // Reset beats per slide
      setAspectRatio("4:3");
      setTextColor("#000000");
      setTextFont("Arial");
      setTextSize(40);
      setLogoUrl("https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoWhiteTM.png?alt=media&token=6ef84cf6-6fd9-4b56-bf05-aea9a1784c72");
      setLogoSize(20);
      setLogoPosition("right");
      setTextAlignment("center");
      setTextBackgroundEnabled(false);
      setTextBackgroundColor("#ffffff");
      setTextBackgroundOpacity(1); // Reset text background opacity
    } catch (error) {
      console.error("Upload error:", error);
      if (error.code === "storage/unauthorized") {
        alert("You do not have permission to upload files. Please check your Firebase Storage rules.");
      } else {
        alert("Failed to upload slideshow. Please try again.");
      }
    }
    setUploading(false);
  };

  useEffect(() => {
    const detectAudioBPM = async () => {
      if (!audioFile) return;
      setBpmDetecting(true);

      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const arrayBuffer = await audioFile.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        const bpm = await analyze(audioBuffer); // Use analyze for BPM detection
        setBpmValue(Math.round(bpm));
      } catch (err) {
        console.error('BPM detection failed:', err);
        alert("Couldn't detect BPM automatically. Please enter it manually.");
      }

      setBpmDetecting(false);
    };

    detectAudioBPM();
  }, [audioFile]); // Add useEffect for BPM detection

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const updateTime = () => setCurrentTime(audio.currentTime);
      audio.addEventListener("timeupdate", updateTime);
      audio.addEventListener("loadedmetadata", () => setAudioDuration(audio.duration));
      return () => {
        audio.removeEventListener("timeupdate", updateTime);
      };
    }
  }, []);

  return (
    <div>
      {/* Home and Navigation Buttons */}
      <div style={{ marginBottom: '20px', textAlign: 'center' }}>
        <button
          onClick={() => navigate('/')}
          style={{
            backgroundColor: '#4CAF50',
            color: 'white',
            padding: '10px 20px',
            marginRight: '10px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Home
        </button>
        <button
          onClick={() => navigate('/educator/slideshow')}
          style={{
            backgroundColor: '#2196F3',
            color: 'white',
            padding: '10px 20px',
            marginRight: '10px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          SlideShow
        </button>
        
      </div>
      <h2>Upload a Slideshow</h2>
      <input
        type="text"
        placeholder="Enter slideshow title"
        value={slideshowTitle}
        onChange={(e) => setSlideshowTitle(e.target.value)}
        
      />
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        style={{ border: "2px dashed #ccc", padding: "20px", marginTop: "10px" }}
      >
        Drag and drop images or videos here
      </div>
      <input
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={handleFileInput}
        style={{
          backgroundColor: '#FFA726',
          color: 'white',
          padding: '10px 20px',
          marginRight: '10px',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer'
        }}
      />
      {slides.map((slide) => (
        <div key={slide.id} style={{ marginTop: "10px" }}>
          {slide.type === "image" ? (
            <img
              src={slide.previewUrl}
              alt="Preview"
              style={{ width: "150px", height: "auto" }}
            />
          ) : (
            <video
              src={slide.previewUrl}
              controls
              style={{ width: "150px", height: "auto" }}
            />
          )}
          <input
            type="text"
            placeholder="Caption"
            value={slide.caption}
            onChange={(e) => handleCaptionChange(slide.id, e.target.value)}
          />
          <input
            type="number"
            placeholder="Order"
            value={slide.order}
            onChange={(e) => handleOrderChange(slide.id, e.target.value)}
            style={{ width: "60px" }}
          />
          <button
            onClick={() => handleDuplicateLocalSlide(slide)}
            style={{
              marginLeft: "10px",
              padding: "4px 8px",
              backgroundColor: "#ff9800",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Duplicate
          </button>
        </div>
      ))}
      <div style={{ marginTop: "20px" }}>
        <label>
          Upload Background Audio:
          <input
            type="file"
            accept="audio/*"
            onChange={(e) => {
              setAudioFile(e.target.files[0]);
              if (audioRef.current) {
                audioRef.current.src = URL.createObjectURL(e.target.files[0]);
              }
            }}
            style={{
              backgroundColor: '#F06292',
              color: 'white',
              padding: '10px 20px',
              marginRight: '10px',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          />
        </label>
        <audio ref={audioRef} controls style={{ marginTop: "10px", width: "100%" }} />
      </div>

      <div style={{ marginTop: "10px" }}>
        <label>
          Set Tempo (BPM):
          <input
            type="number"
            value={bpmValue}
            onChange={(e) => setBpmValue(parseInt(e.target.value) || 60)}
            style={{ marginLeft: "10px", width: "80px" }}
          />
          {bpmDetecting && (
            <span style={{ marginLeft: "10px", fontStyle: "italic", color: "gray" }}>
              Detecting BPM...
            </span>
          )}
        </label>
      </div>
      <div style={{ marginTop: "10px" }}>
        <label>
          Amount of Beats Per Slide:
          <input
            type="number"
            value={beatsPerSlide}
            onChange={(e) => setBeatsPerSlide(parseInt(e.target.value) || 8)}
            style={{ marginLeft: "10px", width: "80px" }}
          />
        </label>
      </div>
      <div style={{ marginTop: "10px" }}>
        <label>
          Aspect Ratio:
          <select
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            style={{ marginLeft: "10px" }}
          >
            <option value="4:3">4:3</option>
            <option value="16:9">16:9</option>
            <option value="9:16">9:16</option>
          </select>
        </label>
      </div>
      <div style={{ marginTop: "10px" }}>
        <label>
          Text Color:
          <input
            type="color"
            value={textColor}
            onChange={(e) => setTextColor(e.target.value)}
            style={{ marginLeft: "10px" }}
          />
        </label>
      </div>
      <div style={{ marginTop: "10px" }}>
        <label>
          Text Font:
          <select
            value={textFont}
            onChange={(e) => setTextFont(e.target.value)}
            style={{ marginLeft: "10px" }}
          >
            <option value="Arial">Arial</option>
            <option value="Verdana">Verdana</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Georgia">Georgia</option>
            <option value="Courier New">Courier New</option>
          </select>
        </label>
      </div>
      <div style={{ marginTop: "10px" }}>
        <label>
          Text Size (px):
          <input
            type="number"
            value={textSize}
            onChange={(e) => setTextSize(parseInt(e.target.value) || 40)}
            style={{ marginLeft: "10px", width: "80px" }}
          />
        </label>
      </div>
      <div style={{ marginTop: "10px" }}>
        <label>
          Logo URL:
          <select
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            style={{ marginLeft: "10px", width: "100%" }}
          >
            {predefinedLogos.map((logo) => (
              <option key={logo.value} value={logo.value}>
                {logo.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div style={{ marginTop: "10px" }}>
        <label>
          Logo Size (%):
          <input
            type="number"
            value={logoSize}
            onChange={(e) => setLogoSize(parseInt(e.target.value) || 20)}
            style={{ marginLeft: "10px", width: "80px" }}
          />
        </label>
      </div>
      <div style={{ marginTop: "10px" }}>
        <label>
          Logo Position:
          <select
            value={logoPosition}
            onChange={(e) => setLogoPosition(e.target.value)}
            style={{ marginLeft: "10px" }}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </label>
      </div>
      <div style={{ marginTop: "10px" }}>
        <label>
          Text Alignment:
          <select
            value={textAlignment}
            onChange={(e) => setTextAlignment(e.target.value)}
            style={{ marginLeft: "10px" }}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </label>
      </div>
      <div style={{ marginTop: "10px" }}>
        <label>
          <input
            type="checkbox"
            checked={textBackgroundEnabled}
            onChange={(e) => setTextBackgroundEnabled(e.target.checked)}
            style={{ marginRight: "10px" }}
          />
          Enable Text Background
        </label>
        {textBackgroundEnabled && (
          <>
            <input
              type="color"
              value={textBackgroundColor}
              onChange={(e) => setTextBackgroundColor(e.target.value)}
              style={{ marginLeft: "10px" }}
            />
            <div style={{ marginTop: "10px" }}>
              <label>
                Background Opacity:
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={textBackgroundOpacity}
                  onChange={(e) => setTextBackgroundOpacity(parseFloat(e.target.value))}
                  style={{ marginLeft: "10px" }}
                />
                <span style={{ marginLeft: "10px" }}>{(textBackgroundOpacity * 100).toFixed(0)}%</span>
              </label>
            </div>
          </>
        )}
      </div>

      <div
        style={{
          position: "relative",
          height: "30px",
          background: "#ddd",
          margin: "20px 0",
          borderRadius: "4px",
        }}
        onClick={(e) => {
          const barWidth = e.currentTarget.offsetWidth;
          const clickX = e.nativeEvent.offsetX;
          const clickedTime = (clickX / barWidth) * audioDuration;
          setMarkers((prev) => [...prev, clickedTime]);
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: `${(currentTime / audioDuration) * 100}%`,
            width: "2px",
            height: "100%",
            backgroundColor: "red",
          }}
        />
        {markers.map((time, index) => (
          <div
            key={index}
            style={{
              position: "absolute",
              left: `${(time / audioDuration) * 100}%`,
              top: 0,
              width: "4px",
              height: "100%",
              backgroundColor: "blue",
            }}
          />
        ))}
      </div>

      {markers.map((time, index) => (
        <div key={index} style={{ marginTop: "10px" }}>
          <label>Slide {index + 1} Time (sec):</label>
          <input
            type="number"
            value={time.toFixed(2)}
            onChange={(e) => {
              const newTime = parseFloat(e.target.value);
              setMarkers((prev) =>
                prev.map((t, i) => (i === index ? newTime : t))
              );
            }}
            style={{ marginLeft: "10px", width: "80px" }}
          />
        </div>
      ))}

      <button
        onClick={handleUpload}
        disabled={uploading}
        style={{
          backgroundColor: '#9C27B0',
          color: 'white',
          padding: '10px 20px',
          marginTop: '20px',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer'
        }}
      >
        {uploading ? "Uploading..." : "Upload Slideshow"}
      </button>
      
    </div>
  );
};

export default SlideUploader;
